/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Inbox, Lock, Search, WifiOff } from 'lucide-react';
import { clsx } from 'clsx';
import Button, {
  buttonBaseClasses,
  buttonSizeClasses,
  buttonVariantClasses,
} from '@components/ui/Button';

/**
 * The "nothing here yet" look, shared so every screen says it the same way.
 * `success` is the good-news empty: nothing to show because all is well.
 * For a FAILED load use `LoadErrorCard` instead — a failed region must read as
 * failed (role="alert" + retry), not as a genuinely empty one (role="status").
 */
export type EmptyStateVariant = 'no-data' | 'no-results' | 'permission' | 'offline' | 'success';

interface EmptyStateCta {
  label: string;
  /**
   * A real destination: renders an actual <Link>, so middle-click, open-in-new-tab
   * and "copy link" all work. Prefer this for plain navigation.
   */
  to?: string;
  /** For an action that isn't navigation (a mutation, opening a modal, etc). */
  onClick?: () => void;
  variant?: 'primary' | 'secondary';
  icon?: ReactNode;
}

interface EmptyStateProps {
  variant: EmptyStateVariant;
  title: string;
  description?: string;
  cta?: EmptyStateCta;
  /** Override the variant's default icon. */
  icon?: ReactNode;
  /** Heading element so the page outline stays valid. Defaults to 3. */
  headingLevel?: 2 | 3 | 4 | 5 | 6;
  /** Card chrome. false = bare centered stack (full-page, or inside an existing card). */
  framed?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

interface VariantSpec {
  iconWell: string;
  defaultIcon: ReactNode;
  defaultCtaVariant: 'primary' | 'secondary';
}

const VARIANTS: Record<EmptyStateVariant, VariantSpec> = {
  'no-data': {
    iconWell: 'bg-primary/10 text-primary',
    defaultIcon: <Inbox aria-hidden />,
    defaultCtaVariant: 'primary',
  },
  'no-results': {
    iconWell: 'bg-muted text-text-secondary',
    defaultIcon: <Search aria-hidden />,
    defaultCtaVariant: 'secondary',
  },
  permission: {
    iconWell: 'bg-status-warning-bg text-status-warning-fg',
    defaultIcon: <Lock aria-hidden />,
    defaultCtaVariant: 'primary',
  },
  offline: {
    iconWell: 'bg-muted text-text-secondary',
    defaultIcon: <WifiOff aria-hidden />,
    defaultCtaVariant: 'secondary',
  },
  success: {
    iconWell: 'bg-status-success-bg text-status-success-fg',
    defaultIcon: <CheckCircle2 aria-hidden />,
    defaultCtaVariant: 'secondary',
  },
};

const SIZES = {
  sm: {
    pad: 'py-8 px-4',
    well: 'w-11 h-11 [&>svg]:w-5 [&>svg]:h-5',
    title: 'text-sm',
    desc: 'text-xs',
  },
  md: {
    pad: 'py-12 px-6',
    well: 'w-14 h-14 [&>svg]:w-6 [&>svg]:h-6',
    title: 'text-base',
    desc: 'text-sm',
  },
} as const;

export function EmptyState({
  variant,
  title,
  description,
  cta,
  icon,
  headingLevel = 3,
  framed = true,
  size = 'md',
  className,
}: EmptyStateProps) {
  const spec = VARIANTS[variant];
  const dims = SIZES[size];
  const Heading = `h${headingLevel}` as const;

  return (
    <div
      role="status"
      className={clsx(
        'flex flex-col items-center justify-center text-center',
        dims.pad,
        framed && 'bg-card border border-border rounded-lg',
        className
      )}
    >
      <div
        className={clsx(
          'rounded-2xl flex items-center justify-center mb-3',
          dims.well,
          spec.iconWell
        )}
      >
        {icon ?? spec.defaultIcon}
      </div>
      <Heading className={clsx('font-semibold text-text-primary', dims.title)}>{title}</Heading>
      {description && (
        <p className={clsx('text-text-muted mt-1.5 max-w-xs', dims.desc)}>{description}</p>
      )}
      {cta &&
        (cta.to ? (
          <Link
            to={cta.to}
            className={clsx(
              buttonBaseClasses,
              buttonVariantClasses[cta.variant ?? spec.defaultCtaVariant],
              buttonSizeClasses.sm,
              'mt-4'
            )}
          >
            {cta.icon}
            {cta.label}
          </Link>
        ) : (
          <Button
            size="sm"
            variant={cta.variant ?? spec.defaultCtaVariant}
            icon={cta.icon}
            onClick={cta.onClick}
            className="mt-4"
          >
            {cta.label}
          </Button>
        ))}
    </div>
  );
}

export default EmptyState;
